import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Globe, KeyRound, Camera, ChartColumn, MapPin, WifiOff, LifeBuoy, ShieldCheck } from 'lucide-react';
import { PublicPage } from './elements';

const FEATURES = [
    { icon: <Camera />, title: 'Documentación de Productos', text: 'Captura fotos y datos en tiempo real' },
    { icon: <ChartColumn />, title: 'Gestión de Inventario', text: 'Monitoreo de niveles de stock' },
    { icon: <MapPin />, title: 'Verificación GPS', text: 'Visitas verificadas por ubicación' },
    { icon: <WifiOff />, title: 'Modo Sin Conexión', text: 'Funciona sin internet' }
];

/**
 * Public Support Page for App Store compliance
 * This page is accessible without authentication
 */
export default function Support() {
    return (
        <PublicPage
            footer={
                <div className="flex flex-col items-center gap-3">
                    <Link to="/privacy-policy" className="inline-flex items-center gap-1.5 font-medium text-primary-600 hover:text-primary-700">
                        <ShieldCheck />
                        Política de Privacidad
                    </Link>
                    <p className="m-0 text-xs text-surface-400">© {new Date().getFullYear()} Shelf. Todos los derechos reservados.</p>
                </div>
            }>
            {/* Header */}
            <div className="mb-8">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-50 text-lg text-primary-600">
                    <LifeBuoy />
                </span>
                <p className="m-0 mt-5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-primary-600">
                    <span>Shelf</span>
                    <span className="text-primary-300">·</span>
                    <span>Auditoría de Tiendas</span>
                </p>
                <h1 className="m-0 mt-1.5 text-[28px] font-semibold tracking-tight text-surface-900 leading-tight">Soporte y Asistencia</h1>
                <p className="m-0 mt-3 text-[15px] leading-relaxed text-surface-600">
                    Shelf es una plataforma profesional de auditoría de retail diseñada para representantes de campo, gerentes de tiendas y equipos de
                    ventas que realizan auditorías de tiendas y seguimiento de inventario en múltiples ubicaciones.
                </p>
            </div>

            <div className="space-y-8">
                {/* Contact Information */}
                <section>
                    <h2 className="m-0 mb-3 text-[15px] font-semibold text-surface-900">Contacto</h2>
                    <div className="grid gap-3 sm:grid-cols-2">
                        <a
                            href="mailto:info@shelfve.com"
                            className="group flex items-center gap-3 rounded-xl border border-surface-200 p-4 transition-colors hover:border-primary-200 hover:bg-primary-50">
                            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-surface-100 text-[16px] text-surface-600 group-hover:bg-white group-hover:text-primary-600">
                                <Mail />
                            </span>
                            <span className="min-w-0">
                                <span className="block text-xs text-surface-500">Email de Soporte:</span>
                                <span className="block truncate text-sm font-medium text-primary-600">info@shelfve.com</span>
                            </span>
                        </a>
                        <a
                            href="https://shelfve.com"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group flex items-center gap-3 rounded-xl border border-surface-200 p-4 transition-colors hover:border-primary-200 hover:bg-primary-50">
                            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-surface-100 text-[16px] text-surface-600 group-hover:bg-white group-hover:text-primary-600">
                                <Globe />
                            </span>
                            <span className="min-w-0">
                                <span className="block text-xs text-surface-500">Sitio Web:</span>
                                <span className="block truncate text-sm font-medium text-primary-600">shelfve.com</span>
                            </span>
                        </a>
                    </div>
                </section>

                {/* Account Access */}
                <section className="flex gap-3 rounded-xl border border-info-border bg-info-bg p-4">
                    <KeyRound className="mt-0.5 shrink-0 text-[16px] text-info-fg" />
                    <div>
                        <h2 className="m-0 mb-1 text-sm font-semibold text-surface-900">Acceso a la Aplicación</h2>
                        <p className="m-0 text-sm leading-relaxed text-surface-700">
                            Esta aplicación es para empleados autorizados. Las cuentas de usuario son administradas por los administradores de la empresa. Para
                            solicitar acceso, contacta al administrador de Shelf de tu organización o envía un email a{' '}
                            <a href="mailto:info@shelfve.com" className="font-medium text-primary-600 hover:underline">
                                info@shelfve.com
                            </a>
                        </p>
                    </div>
                </section>

                {/* Features */}
                <section>
                    <h2 className="m-0 mb-3 text-[15px] font-semibold text-surface-900">Características Principales</h2>
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                        {FEATURES.map((f) => (
                            <div key={f.title} className="flex items-start gap-3 rounded-xl border border-surface-200 p-4">
                                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-[15px] text-primary-600">
                                    {f.icon}
                                </span>
                                <div>
                                    <h3 className="m-0 text-sm font-medium text-surface-900">{f.title}</h3>
                                    <p className="m-0 mt-0.5 text-sm text-surface-500">{f.text}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>
            </div>
        </PublicPage>
    );
}
